import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { AffectedTrain } from "features/CenterContent/RoleContent/Vaktleder/types";
import {
  DirectionEnum,
  GroupedTrainFormSchema,
  TrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import { singleTrainFormToRequestBody } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { TrainInfoMessage } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { getDefaultPrognosis } from "features/CenterContent/shared/operationalInformation/prognosisUtils";
import { stopSchema } from "features/CenterContent/shared/operationalInformation/utils";
import { TrainsGroupedBySeries } from "features/History/groupTrainSeries";
import { TFunction } from "i18next";
import { Semantics } from "shared/components/drops/DropsBadge";
import { z } from "zod";

export const getInfoTitleFromParams = (
  translate: TFunction<"translation", undefined>,
  type: string,
  fromStop: string | z.infer<typeof stopSchema> | null | undefined,
  title: string | null,
) => {
  const prefacedFromStop = (preface: string) => {
    if (!fromStop) {
      return "";
    }

    // Handle string case (for TrainResumed)
    if (typeof fromStop === "string") {
      return ` ${preface} ${fromStop}`;
    }

    // Handle stopSchema object case (for TrainInfoMessage)
    return ` ${preface} ${fromStop.name}`;
  };

  switch (type) {
    case "TRAIN_CANCELLED":
      return translate("Innstilt") + prefacedFromStop(translate("fra")); // Tog innstilt
    case "TRAIN_STOPPING_EXTRA_AT_STATION":
      return translate("Stopper ekstra") + prefacedFromStop(translate("på")); // Stopper ekstra på stasjon
    case "TRAIN_DELAYED":
      return translate("Forsinket") + prefacedFromStop(translate("fra")); // Tog forsinket
    case "TRAIN_CAPACITY_REDUCED":
      return translate("Redusert kapasitet"); // Redusert setekapasitet
    case "TRAIN_CAPACITY_INCREASED":
      return translate("Økt kapasitet"); // Økt setekapasitet
    case "TRAIN_STOPPED":
      return `Står${prefacedFromStop(translate("på"))}`; // Tog står
    case "TRAIN_NOT_STOPPING_AT_STATION":
      return `Stopper ikke${prefacedFromStop(translate("på"))}`; // Stopper ikke på stasjon
    case "TRAIN_LATE_TO_TRACK":
      return translate("Sent satt opp"); // Sent satt opp (ikke forsinket)
    case "TRAIN_DELAY_EXPECTED":
      return translate("Forsinkelse forventes"); // Forsinkelse forventes
    case "TRAIN_MISSING_PRODUCT":
      return translate("Produkt mangler"); // Produkt mangler
    case "TRAIN_CLOSED_SET":
      return translate("Avstengt togsett"); // Avstengt togsett
    default:
      return title ?? translate("Generell informasjon");
  }
};

const eventTypeToSemanticsMap: { [key: string]: Semantics } = {
  // Train events
  TRAIN_CANCELLED: "alarm",
  TRAIN_NOT_STOPPING_AT_STATION: "alarm",
  TRAIN_CHANGED_ROUTE: "alarm",
  TRAIN_GENERAL_WARNING: "alarm",
  TRAIN_DELAYED: "warning",
  TRAIN_DELAY_EXPECTED: "warning",
  TRAIN_STOPPED: "warning",
  TRAIN_STOPPING_AND_WAITING_AT_STATION: "warning",
  TRAIN_STOPPING_EXTRA_AT_STATION: "warning",
  TRAIN_LATE_TO_TRACK: "info",
  TRAIN_CAPACITY_REDUCED: "info",
  TRAIN_CAPACITY_INCREASED: "info",
  TRAIN_CLOSED_SET: "info",
  TRAIN_MISSING_PRODUCT: "info",
  TRAIN_GROUP_TRAVEL: "info",
  TRAIN_WHEELCHAIR_PASSENGER: "info",
  TRAIN_REDUCED_MOBILITY_PASSENGER: "info",
  TRAIN_VISUALLY_IMPAIRED_PASSENGER: "info",
  TRAIN_GENERAL_INFO: "info",
  TRAIN_GENERAL: "warning",
  TRAIN_UNKNOWN_EVENT: "info",
  // Infrastructure events
  INFRASTRUCTURE_STRETCH_CLOSED: "alarm",
  INFRASTRUCTURE_GENERAL_WARNING: "alarm",
  INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED: "secondaryAlarm",
  INFRASTRUCTURE_SLOW_DRIVING: "warning",
  INFRASTRUCTURE_GENERAL_INFO: "info",
  INFRASTRUCTURE_PLANNED_MAINTENANCE: "info",
};

export const getEventSemantics = (key: string): Semantics =>
  eventTypeToSemanticsMap[key] || "info";
/**
 * Create shorter names for train info, with fallback to title. CLOSED events will be
 * translated to "Friskmeldt".
 */
export const getInfoTitle = (
  trainInfo: TrainInfoMessage,
  translate: TFunction<"translation", undefined>,
) => {
  const { type, fromStop, title } = trainInfo;
  return getInfoTitleFromParams(translate, type, fromStop, title);
};

// Find last digit, as last char in identifier is not necessarily last char in identifier
const getDirectionDigit = (identifier: string) => {
  const lastDigit = identifier
    .split("")
    .findLast((c) => !Number.isNaN(parseInt(c, 10)));
  return lastDigit !== undefined ? parseInt(lastDigit, 10) : -1;
};

const trainIdIsEven = (trainId: string) => {
  const directionDigit = getDirectionDigit(trainId);
  if (directionDigit === -1) {
    return false;
  }
  return directionDigit % 2 === 0;
};

export const getDirectionText = (identifier: string): DirectionEnum =>
  trainIdIsEven(identifier) ? "EVEN" : "ODD";

export const getDirection = ({ identifier }: TrainIdentifier): DirectionEnum =>
  trainIdIsEven(identifier) ? "EVEN" : "ODD";

// Assume last digit in given trainIdentifier dictates direction
export const findRepresentativeTrainId = (
  trains: AffectedTrain[],
  evenOrOdd: "even" | "odd",
): TrainIdentifier | undefined => {
  const evenOrOddTrainId = trains.find((train) =>
    evenOrOdd === "even"
      ? trainIdIsEven(train.trainId.identifier)
      : !trainIdIsEven(train.trainId.identifier),
  );

  return evenOrOddTrainId?.trainId ?? undefined;
};

export type InfrastructureConsequence =
  | "TRAIN_CANCELLATIONS"
  | "TRAIN_DELAYS"
  | "TRAIN_CANCELLATIONS_AND_DELAYS";

export const mapInfrastructureConsequence = (
  infraConsequence: InfrastructureConsequence | undefined,
): TrainEventTypeEnum.TRAIN_CANCELLED | TrainEventTypeEnum.TRAIN_DELAYED => {
  switch (infraConsequence) {
    case "TRAIN_DELAYS":
      return TrainEventTypeEnum.TRAIN_DELAYED;

    case "TRAIN_CANCELLATIONS":
    case "TRAIN_CANCELLATIONS_AND_DELAYS":
    case undefined:
      return TrainEventTypeEnum.TRAIN_CANCELLED;
  }
};

const mapInfrastructureEventToTrainEvent = (data: InfrastructureResponse) => {
  const { consequence, action, reason, incidentId } = data;

  const schema: Partial<TrainFormSchema> = {
    type: mapInfrastructureConsequence(
      consequence?.type as InfrastructureConsequence,
    ),
    action,
    reason: reason ?? undefined,
    dropsLogText: { enabled: false, texts: { NOB: "" } },
    incidentId: incidentId ?? undefined,
  };

  return schema;
};

export const trainIdKey = (trainId: TrainIdentifier) =>
  `${trainId.countryCode}_${trainId.identifier}_${trainId.nominalDate}`;

// Constructs an initial batch form state from the selected train groups
export function createInitialBatchFormStateFromTrainSeries(
  trainSeries: TrainsGroupedBySeries,
  infrastructureEvent?: InfrastructureResponse,
): GroupedTrainFormSchema {
  const partialFormFromInfrastructureEvent = infrastructureEvent
    ? mapInfrastructureEventToTrainEvent(infrastructureEvent)
    : {};

  const groupedFormState = Object.entries(trainSeries).map(
    ([trainSeriesKey, affectedTrains]) => {
      const [oddTrains, evenTrains] = affectedTrains.reduce(
        ([odd, even]: AffectedTrain[][], current: AffectedTrain) =>
          trainIdIsEven(current.trainId.identifier)
            ? [odd, [...even, current]]
            : [[...odd, current], even],
        [[], []],
      );

      const oddGroup = oddTrains.length
        ? {
            trainSeries: trainSeriesKey,
            groupLabel: trainSeriesKey,
            route: oddTrains[0].eventStretchName, // TODO: Should be route but we don't have that data here yet
            direction: "ODD",
            trainIdsInGroup: oddTrains.map<TrainIdentifier>(
              (train) => train.trainId,
            ),
            trainForm: {
              type: undefined,
              reason: { type: "" },
              action: { type: "" },
              ...getDefaultPrognosis(),
              dropsLogText: { enabled: false, texts: { NOB: "" } },
              ...partialFormFromInfrastructureEvent,
            },
          }
        : undefined;

      const evenGroup = evenTrains.length
        ? {
            trainSeries: trainSeriesKey,
            groupLabel: trainSeriesKey,
            route: evenTrains[0].eventStretchName, // TODO: Should be route but we don't have that data here yet
            direction: "EVEN",
            trainIdsInGroup: evenTrains.map<TrainIdentifier>(
              (train) => train.trainId,
            ),
            trainForm: {
              type: undefined,
              reason: { type: "" },
              action: { type: "" },
              ...getDefaultPrognosis(),
              dropsLogText: { enabled: false, texts: { NOB: "" } },
              ...partialFormFromInfrastructureEvent,
            },
          }
        : undefined;

      return [
        trainSeriesKey,
        [oddGroup, evenGroup].filter((g) => g !== undefined),
      ];
    },
  );

  return Object.fromEntries(groupedFormState);
}

export const batchTrainFormToRequestBody = (
  formState: GroupedTrainFormSchema,
): TrainInfoRequest[] => {
  const groups = Object.values(formState).flatMap((a) => a);

  return groups
    .flatMap((group) =>
      group.trainIdsInGroup.map((trainId) =>
        singleTrainFormToRequestBody(group.trainForm, trainId),
      ),
    )
    .filter((body) => body !== null);
};
